import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { FileText, CheckCircle2, AlertTriangle, Loader2, PenLine } from 'lucide-react';
import { toast } from 'sonner';
import { SignaturePad } from '../components/SignaturePad';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/public/sign`;
const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' }) : '');

const StatusScreen = ({ icon: Icon, color, title, text, pdfUrl, testid }) => (
  <div className="text-center py-10" data-testid={testid}>
    <Icon className={`w-14 h-14 mx-auto mb-4 ${color}`} strokeWidth={1.6} />
    <h2 className="font-display text-2xl text-[#2B3A2C] mb-2">{title}</h2>
    <p className="text-gray-600 text-sm max-w-sm mx-auto">{text}</p>
    {pdfUrl && (
      <a href={pdfUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 mt-5 rounded-xl bg-[#2B3A2C] text-white px-4 py-2.5 text-sm font-semibold" data-testid="sign-view-signed-pdf">
        <FileText className="w-4 h-4" /> Voir le document signé
      </a>
    )}
  </div>
);

export default function SignDocument() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState('');
  const [name, setName] = useState('');
  const [accept, setAccept] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/${token}`)
      .then((r) => { setInfo(r.data); setName(r.data.signer_name || ''); })
      .catch((e) => setError(e?.response?.data?.detail || 'Lien invalide'));
  }, [token]);

  const sign = async (signature) => {
    if (!name.trim()) { toast.error('Indiquez votre nom complet'); return; }
    if (!accept) { toast.error('Cochez la case pour confirmer avoir lu le document'); return; }
    setBusy(true);
    try {
      const r = await axios.post(`${API}/${token}`, { signature_base64: signature, signer_name: name.trim(), accept });
      setInfo((i) => ({ ...i, status: 'signed', signed_at: r.data.signed_at, signed_name: name.trim() }));
    } catch (e) { toast.error(e?.response?.data?.detail || 'Signature impossible'); } finally { setBusy(false); }
  };

  const pdfUrl = `${API}/${token}/pdf`;
  let content;
  if (error) content = <StatusScreen icon={AlertTriangle} color="text-[#B42318]" title="Lien invalide" text={error} testid="sign-error" />;
  else if (!info) content = <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" /></div>;
  else if (info.status === 'signed') content = <StatusScreen icon={CheckCircle2} color="text-emerald-600" title="Document signé" text={`Merci ${info.signed_name || ''}. Signature enregistrée le ${fmt(info.signed_at)}. L'agence ${info.agency_name} a reçu votre signature.`} pdfUrl={pdfUrl} testid="sign-done" />;
  else if (info.status === 'expired') content = <StatusScreen icon={AlertTriangle} color="text-[#B85C38]" title="Lien expiré" text={`Ce lien n'est plus valable. Demandez un nouveau lien à ${info.agency_name}.`} testid="sign-expired" />;
  else content = (
    <div className="space-y-5" data-testid="sign-form">
      <div>
        <p className="text-[11px] uppercase tracking-[0.25em] font-semibold text-[#B85C38]">{info.doc_label}</p>
        <h1 className="font-display text-2xl text-[#2B3A2C] mt-1" data-testid="sign-doc-title">{info.title}</h1>
        <p className="text-sm text-gray-600 mt-1">Envoyé par <b>{info.agency_name}</b> · lien valable jusqu'au {fmt(info.expires_at)}</p>
      </div>
      <a href={pdfUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-xl border-2 border-[#2B3A2C] text-[#2B3A2C] px-4 py-3 text-sm font-bold hover:bg-[#2B3A2C] hover:text-white transition-colors" data-testid="sign-view-pdf">
        <FileText className="w-4 h-4" /> Lire le document (PDF)
      </a>
      <label className="block text-xs font-semibold text-gray-600">Votre nom complet
        <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#B85C38] outline-none" data-testid="sign-name-input" />
      </label>
      <label className="flex items-start gap-2 text-sm text-gray-700 cursor-pointer">
        <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} className="mt-1 accent-[#B85C38]" data-testid="sign-accept-checkbox" />
        J'ai lu le document et j'accepte de le signer électroniquement.
      </label>
      <div>
        <p className="text-xs font-semibold text-gray-600 mb-1 flex items-center gap-1"><PenLine className="w-3.5 h-3.5" /> Signez dans le cadre</p>
        {busy ? <div className="flex justify-center py-10"><Loader2 className="w-8 h-8 animate-spin text-[#B85C38]" /></div>
          : <SignaturePad onSave={sign} onCancel={() => setAccept(false)} width={Math.min(340, (typeof window !== 'undefined' ? window.innerWidth : 400) - 90)} height={170} />}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F5EFE1] flex items-start justify-center px-4 py-8">
      <div className="w-full max-w-md rounded-3xl bg-white shadow-xl border border-[#B85C38]/15 p-5 overflow-hidden" data-testid="sign-document-page">
        {content}
      </div>
    </div>
  );
}
